import { addDays, addYears, format as formatDate, startOfDay, subMonths } from 'date-fns';
import { toast } from 'sonner';
import { en as commonEn, th as commonTh } from '@/modules/shared/i18n/locales';
import { getStoredToken } from '@/modules/shared/utils';
import { setAxiosHeaders } from '@/modules/auth/shared/hooks/useTokenManager';
import { useAppStore } from '@/modules/shared/store/appStore';
import { FAIRDEE_LINE_URL, FAIRDEE_MLM_LINE_URL } from '../constants';

/** Loose record for legacy data blobs (affiliate, user, API payloads). */
type AnyRecord = Record<string, any>;

/**
 * Get the base URL for public assets
 * In Vite, assets are referenced directly from the root, not using PUBLIC_URL
 */
const LOCAL_ITEMS = [
  'accessToken',
  'language',
  'statusFilter',
  'user',
  'inlineFilterData',
  'compareData',
  'filters',
  'insurerId',
  'insurerName',
  'userId',
  'carId',
  'quotationQueryId',
  'quotationId',
  'pipDateFilter',
  'pipFilter',
  'leadsFilter',
  'nonMotorFilter',
  'nonMotorFormFilter',
  'coaFilter',
  'refreshToken',
  'referralsFilter',
  'firebaseToken',
  'firebaseTokenId',
  'lineTarget',
  'is_mlm',
  'currentAffiliate',
  'source',
  'qr_data',
  'income_selected_date',
  'prepaid_qr_data',
  'qrPaidNotification',
  'subagent_portal_filters',
];

/**
 * Valid hostnames for the application
 */
export const validHostNames: Record<string, boolean> = {
  'fairdee.co.th': true,
  'www.fairdee.co.th': true,
};

export const getParameterByName = (name: string, url?: string): string | null =>
  new URL(url ?? window.location.href).searchParams.get(name);

// const STORAGE_TYPE = window.location.pathname === '/line-landing' ? 'session' : 'local';
const STORAGE_TYPE = 'local';
export const setLocalData = (key: string, value: any, storage_type = 'local') => {
  if (!['local', 'session'].includes(storage_type)) {
    storage_type = STORAGE_TYPE;
  }
  if (typeof value === 'object') {
    value = JSON.stringify(value);
  }

  if (storage_type === 'local') {
    window.localStorage.setItem(key, value);
  } else if (storage_type === 'session') {
    window.sessionStorage.setItem(key, value);
  }
};

export const getLocalData = (key: string, storage_type = 'local'): any => {
  if (!['local', 'session'].includes(storage_type)) {
    storage_type = STORAGE_TYPE;
  }
  if (storage_type === 'local') {
    let returnData: any = window.localStorage.getItem(key) || null;
    try {
      returnData = JSON.parse(returnData);
    } catch (e) {
      return returnData;
    }
    return returnData;
  } else if (storage_type === 'session') {
    let returnData: any = window.sessionStorage.getItem(key) || null;
    try {
      returnData = JSON.parse(returnData);
    } catch (e) {
      return returnData;
    }
    return returnData;
  }
};

if (!Boolean(getLocalData('rememberMe'))) {
  LOCAL_ITEMS.push('accessToken');
}

export const deleteLocalData = (key: string, storage_type = 'local') => {
  if (!['local', 'session'].includes(storage_type)) {
    storage_type = STORAGE_TYPE;
  }
  if (storage_type === 'local') {
    window.localStorage.removeItem(key);
  } else if (storage_type === 'session') {
    window.sessionStorage.removeItem(key);
  }
};

export const clearLocalData = (storage_type = 'local') => {
  // Simple implementation without hot reload protection logic
  if (!['local', 'session'].includes(storage_type)) {
    storage_type = STORAGE_TYPE;
  }

  if (storage_type === 'local') {
    LOCAL_ITEMS.forEach((item) => {
      window.localStorage.removeItem(item);
    });
    // Also clear all localStorage to ensure complete cleanup
    window.localStorage.clear();
  } else if (storage_type === 'session') {
    LOCAL_ITEMS.forEach((item) => {
      window.sessionStorage.removeItem(item);
    });
    // Also clear all sessionStorage to ensure complete cleanup
    window.sessionStorage.clear();
  }
};

export const getAccessToken = (): string | null => getStoredToken('accessToken');

export const setAccessToken = (token: string) => {
  setLocalData('accessToken', token);
};

export const setRefreshToken = (token: string) => {
  setLocalData('refreshToken', token);
};

export const getRefreshToken = (): string | null => getStoredToken('refreshToken');

export const getUserData = (): any => {
  let userProfile = useAppStore.getState().user;

  if (userProfile && Object.keys(userProfile).length) {
    return userProfile;
  }
  return null;
};

export const setUserData = (user: any) => {
  setLocalData('user', user);
  useAppStore.getState().setUser(user);
};

export const unregisterTracking = () => {
  const trackEvent = (window as any).trackEvent;
  trackEvent && trackEvent.unRegister();
};

// `setAxiosHeaders` is imported above for `updateTokensFromCookie` below. The three axios header
// helpers now live only in `@/modules/auth/shared/hooks/useTokenManager` — this file re-exported
// them briefly while its callers were repointed. Import them from there.

/**
 * Clear all cookies by setting them to expire in the past
 */
export const clearCookies = () => {
  const cookieJar = getCookieJar();
  const possibleCookieNames = [
    'access_token',
    'accessToken',
    'token',
    'jwt',
    'auth_token',
    'authToken',
    'refresh_token',
    'refreshToken',
    'refresh',
  ];

  // Clear all possible auth cookies
  possibleCookieNames.forEach((name) => {
    // Set cookie to expire in the past (clears it)
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
  });

  // Also clear any cookies found in cookieJar
  Object.keys(cookieJar).forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
  });
};

export const getLanguage = (): string => {
  try {
    const localLanguage = getLocalData('language') || 'thai';
    setLanguageToElement(localLanguage);
    return localLanguage;
  } catch (error) {
    console.warn('Error accessing language from store:', error);
    const localLanguage = getLocalData('language') || 'thai';
    setLanguageToElement(localLanguage);
    return localLanguage;
  }
};

export const setLanguageToElement = (lang: string) => {
  // $(
  //   'body, div, p, label, span, img, a, ul, li, nav, header, footer, h1, h2, h3, h4, h5, button, input, textarea, select'
  // ).attr('lang', language || 'thai');
};

export const isMobileDevice = (): boolean => {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    return true;
  }
  return false;
};

const googleEvent = (data: AnyRecord, to?: string) => {
  const ga = (window as any).ga;
  if (to && ga) {
    ga(to + '.send', data);
  }
  if (validHostNames[window.location.hostname] && ga) {
    ga('send', data);
  }
};

export const snakeToTitleCase = (str: string | number | null | undefined): string => {
  if (str === 0 || !str) {
    return str as unknown as string;
  }
  str = str.toString();
  return str
    .split('_')
    .map((item) => item.charAt(0).toUpperCase() + item.substring(1))
    .join(' ');
};

/**
 * Gets URL parameters as an object
 */
/**
 * Bangkok wall clock with the Buddhist year (Gregorian + 543).
 *
 * Was `moment(date).add(543,'years').tz('Asia/Bangkok').format(format)`. moment and
 * moment-timezone were used in this file alone, for this and four date-arithmetic calls, while
 * date-fns is used across 57 files — together they cost about 200 KB of the entry chunk that
 * every page parses. `Intl.DateTimeFormat` does the zone conversion with data the browser
 * already ships.
 *
 * `addYears` rather than adding 543 to the year string, so 29 February clamps to the 28th
 * exactly as moment did and as `toThaiDateTime` in modules/shared/utils.ts already does.
 *
 * Checked against moment over 4,000 random instants plus leap days and Bangkok midnight
 * boundaries: identical everywhere from 1970 on. The single disagreement is a 1941 date, where
 * moment's bundled data and the system's differ on Thailand's pre-war offset.
 */
const BANGKOK_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Bangkok',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const toThaiDateTimeBase = (date: any, format = 'YYYY-MM-DD HH:mm:ss'): string => {
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Invalid date';

  const parts = Object.fromEntries(
    BANGKOK_PARTS.formatToParts(parsed)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  ) as Record<string, string>;

  const thai = addYears(new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day)), 543);

  return (
    format
      .replace('YYYY', String(thai.getFullYear()))
      .replace('MM', pad2(thai.getMonth() + 1))
      .replace('DD', pad2(thai.getDate()))
      // Intl reports midnight as hour 24 in some locales; the rest of the app expects 00.
      .replace('HH', parts.hour === '24' ? '00' : parts.hour)
      .replace('mm', parts.minute)
      .replace('ss', parts.second)
  );
};

export const objToUrlParams = (obj: Record<string, any>): string => {
  let str = '';
  for (let key in obj) {
    if (str != '') {
      str += '&';
    }
    str += key + '=' + encodeURIComponent(obj[key]);
  }
  return '?' + str;
};

const notify = (message: string, type?: string) => {
  try {
    const processedMessage = snakeToTitleCase(message);
    switch (type) {
      case 'error':
        toast.error(processedMessage);
        break;
      case 'success':
        toast.success(processedMessage);
        break;
      case 'warning':
        toast.warning(processedMessage);
        break;
      case 'info':
        toast.info(processedMessage);
        break;
      default:
        toast(processedMessage);
    }
  } catch (error) {
    console.error('Error in notify function:', error);
  }
};

export const errorHandler = (error: any, notification = false): AnyRecord => {
  const MESSAGES: Record<string, string> = (useAppStore.getState().language === 'en' ? commonEn : commonTh)
    .errorMessages;

  let errorObj: AnyRecord = {};

  // Default error fallback
  if (!error) {
    errorObj = {
      error: MESSAGES['Something Went Wrong!'] || 'Something Went Wrong!',
    };
  }

  // 500 error handling
  if (error && error.status === 500) {
    errorObj = {
      error: MESSAGES['Something Went Wrong!'] || 'Something Went Wrong!',
    };
  }

  // Handle error.data.errors (Object or String)
  if (
    error &&
    error.data &&
    error.data.errors &&
    (error.data.errors.constructor === Object || error.data.errors.constructor === String)
  ) {
    if (error.data.errors.constructor === Object) {
      Object.keys(error.data.errors).forEach((key) => {
        if (Array.isArray(error.data.errors[key])) {
          error.data.errors[key].forEach((data: any) => {
            errorObj[key] = MESSAGES[data] || data;
          });
        } else if (error.data.errors[key].constructor === Object) {
          Object.keys(error.data.errors[key]).forEach((k) => {
            errorObj[k] = MESSAGES[error.data.errors[key][k]] || error.data.errors[key][k];
          });
        } else if (error.data.errors[key].constructor === String) {
          errorObj[key] = MESSAGES[error.data.errors[key] as string] || error.data.errors[key];
        }
      });
    } else if (error.data.errors.constructor === String) {
      errorObj['error'] = error.data.errors;
    }
  }
  // Handle error.data.response (Object or String)
  else if (
    error &&
    error.data &&
    error.data.response &&
    (error.data.response.constructor === Object || error.data.response.constructor === String)
  ) {
    if (error.data.response.constructor === Object) {
      Object.keys(error.data.response).forEach((key) => {
        if (error.data.response[key].constructor === String) {
          errorObj[key] = MESSAGES[error.data.response[key] as string] || error.data.response[key];
        } else {
          Object.keys(error.data.response[key]).forEach((k) => {
            errorObj[key] = MESSAGES[error.data.response[key][k]] || error.data.response[key][k];
          });
        }
      });
    } else if (error.data.response.constructor === String) {
      errorObj['error'] = error.data.response;
    }
  }
  // Handle general error.data (Object)
  else if (error && error.data && !error.data.errors && error.data.constructor === Object) {
    Object.keys(error.data).forEach((key) => {
      if (Array.isArray(error.data[key])) {
        error.data[key].forEach((data: any) => {
          errorObj[key] = MESSAGES[data] || data;
        });
      } else if (error.data[key] && error.data[key].constructor === Object && error.data[key] !== null) {
        Object.keys(error.data[key]).forEach((k) => {
          errorObj[k] = MESSAGES[error.data[key][k]] || error.data[key][k];
        });
      } else if (error.data[key]?.constructor === String) {
        errorObj[key] = error.data[key];
      }
    });
  }

  // Handle error.data.detail
  if (error && error.data && error.data.detail) {
    errorObj = {
      ...errorObj,
      message: MESSAGES[error.data.detail] || error.data.detail,
    };
  }

  // Show notifications if requested
  if (notification) {
    Object.keys(errorObj).forEach((key, index) => {
      ((i) => {
        setTimeout(
          () => {
            const displayKey = MESSAGES[key] || key;
            const errorMessage = errorObj[key];
            notify(`${displayKey} -> ${errorMessage}`, 'error');
          },
          1000 + 2000 * i,
        );
      })(index);
    });
  }

  return errorObj;
};

function addListenerEvent(e: Event) {
  const target = e.target as HTMLElement;
  googleEvent({
    hitType: 'event',
    eventCategory: 'click',
    eventAction: target.innerText,
    eventLabel: target.innerText + ' - ' + target.baseURI,
  });
}

export const addClickListeners = () => {
  function myFunction(e: Event) {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' && target.innerText) {
      addListenerEvent(e);
    }
  }
  window.addEventListener('click', myFunction, false);
};

const isMember = (affiliate?: AnyRecord | null, ignoreAdmin?: boolean): boolean => {
  let data: AnyRecord = affiliate || useAppStore.getState().affiliate || {};
  let user: AnyRecord = useAppStore.getState().user || {};
  // Safety check for logged out state
  // NOTE: `.lenght` is a legacy typo preserved as-is — this branch never fires.
  if (!Object.keys(data).length && (Object.keys(user) as any).lenght) {
    return false;
  }

  if (!data.has_broker_license && (!user.roles || !user.roles.includes('admin')) && !ignoreAdmin) {
    return true;
  }
  if (ignoreAdmin && !data.has_broker_license) {
    return true;
  }
  return false;
};

export const isAgent = (): boolean => {
  let data: AnyRecord = useAppStore.getState().affiliate || {};
  let user: AnyRecord = useAppStore.getState().user || {};
  if (data.has_broker_license && (!user.roles || (user.roles.length && !user.roles.includes('admin')))) {
    return true;
  }
  return false;
};

export const isInspectionGarage = (affiliate: AnyRecord | null = null): boolean => {
  affiliate = affiliate || useAppStore.getState().affiliate || {};
  if (affiliate.account_type === 'inspection_garage') {
    return true;
  }
  return false;
};

export const isSubAgent = (affiliate: AnyRecord | null = null): boolean => {
  affiliate = affiliate || useAppStore.getState().affiliate || {};
  if (affiliate.account_type === 'subagent') {
    return true;
  }
  return false;
};

export const isMlmAgent = (affiliate: AnyRecord | null = null): boolean => {
  affiliate = affiliate || useAppStore.getState().affiliate || {};
  if (affiliate.account_type === 'mlm_agent') {
    return true;
  }
  return false;
};

const isDirectAgent = (affiliate: AnyRecord | null = null): boolean => {
  affiliate = affiliate || useAppStore.getState().affiliate || {};
  if (!isMember(affiliate) && affiliate && !affiliate.account_type) {
    return true;
  }
  return false;
};

export const isAdmin = (): boolean => {
  try {
    const user: AnyRecord | null = useAppStore.getState().user;
    if (user && user.roles && user.roles.includes('admin')) {
      return true;
    }
    return false;
  } catch (error) {
    console.warn('Error when checking isAdmin', error);
    return false;
  }
};

export const isSuperAdmin = (): boolean => {
  try {
    const user: AnyRecord | null = useAppStore.getState().user;
    if (user && user.roles && user.roles.includes('super_admin')) {
      return true;
    }
    return false;
  } catch (error) {
    console.warn('Error when checking isSuperAdmin', error);
    return false;
  }
};

export const detectPlatform = (): string => {
  let standalone = (window.navigator as any).standalone,
    userAgent = window.navigator.userAgent.toLowerCase(),
    safari = /safari/.test(userAgent),
    ios = /iphone|ipod|ipad/.test(userAgent),
    view: string;

  if (ios) {
    if (!standalone && safari) {
      // Safari
      view = 'safari';
    } else if (!standalone && !safari) {
      // iOS webview
      view = 'ios-wv';
    } else {
      view = null as unknown as string;
    }
  } else {
    if (userAgent.includes('wv')) {
      view = 'android-wv';
    } else {
      view = 'chrome';
    }
  }
  return view;
};

export const getLineUrl = (): string => {
  if (isAgent() && !isMlmAgent()) {
    return FAIRDEE_LINE_URL;
  }
  return FAIRDEE_MLM_LINE_URL;
};

// Convers cookie into key value pair Object
const getCookieJar = (): Record<string, string> => {
  return document.cookie
    .split(';')
    .map((pair) => pair.split('='))
    .filter((pair) => pair.length === 2)
    .reduce((retVal, pair) => ({ ...retVal, [pair[0].trim()]: pair[1].trim() }), {});
};

// To be used for internal redirection only: in case internally redirected, pick cookie token and set in localstorage
export const updateTokensFromCookie = () => {
  const cookieJar = getCookieJar();

  // Try multiple possible cookie names for access and refresh tokens
  const possibleAccessTokens = ['access_token', 'accessToken', 'token', 'jwt', 'auth_token', 'authToken'];
  const possibleRefreshTokens = ['refresh_token', 'refreshToken', 'refresh'];

  let accessTokenFound = false;
  let refreshTokenFound = false;

  // Look for access token
  for (const name of possibleAccessTokens) {
    if (cookieJar[name]) {
      setAccessToken(cookieJar[name]);
      setAxiosHeaders(cookieJar[name]);
      accessTokenFound = true;
      break;
    }
  }

  // Look for refresh token
  for (const name of possibleRefreshTokens) {
    if (cookieJar[name]) {
      setRefreshToken(cookieJar[name]);
      refreshTokenFound = true;
      break;
    }
  }

  // Original implementation (fallback)
  if (!accessTokenFound && cookieJar.access_token) {
    setAccessToken(cookieJar.access_token);
  }

  if (!refreshTokenFound && cookieJar.refresh_token) {
    setRefreshToken(cookieJar.refresh_token);
  }
};

export const needsRedirection = (affiliate: AnyRecord): boolean => {
  if (isAdmin() || isSuperAdmin()) {
    return false;
  }
  if (affiliate.id && affiliate.block_new_portal === true && (isInspectionGarage(affiliate) || isSubAgent(affiliate))) {
    return false;
  }
  if ((affiliate.id && isMlmAgent(affiliate)) || isDirectAgent(affiliate)) {
    return true;
  }
  if (affiliate.id && !isAdmin() && !isSuperAdmin() && affiliate.block_new_portal === false) {
    return true;
  }
  return false;
};

export const includeDateCut = (url = '', params: AnyRecord = {}, flow = 'quotationReport'): AnyRecord => {
  // Ensure url is a string to prevent null/undefined errors
  const urlString = url || '';

  if (getParameterByName('type') && getParameterByName('type') === 'renewals') {
    if (!urlString.includes('policy_end_date_after') && !params.hasOwnProperty('policy_end_date_after')) {
      params.policy_end_date_after = formatDate(subMonths(new Date(), 3), 'yyyy-MM-dd');
      params.policy_end_date_before = formatDate(addDays(new Date(), 1), 'yyyy-MM-dd');
    }

    return params;
  }

  // Only set default date values if the parameters don't exist in the URL or params object
  if (
    (flow === 'quotationReport' || flow === 'leadsReport') &&
    !urlString.includes('created_at__lte') &&
    !params.hasOwnProperty('created_at__lte')
  ) {
    params.created_at__lte = startOfDay(addDays(new Date(), 1)).toISOString();
    params.created_at__gte = startOfDay(subMonths(new Date(), 1)).toISOString();
  }

  return params;
};
